import { DayFlag, SelectionState, UI } from 'react-day-picker';
import type { ClassNames } from 'react-day-picker';

import { cva } from 'class-variance-authority';

import { FLOATING_LABEL_MOTION } from '../styles/floatingLabelMotion.recipe';
import { focusRing } from '../styles/focusRing';
import { PHONE_INPUT_SIZE } from '../styles/phoneInputSize';

/**
 * Date picker class recipes — the field, and the portalled calendar.
 *
 * Like `Button.recipe`, `TextInput.recipe` and `TextArea.recipe`, these contain ZERO `dark:`
 * variants: colour comes entirely from the `--ds-datepicker-*` slots that `.ds-datepicker` sets in
 * `packages/design-system/src/styles/design-system.css`. The stylesheet owns the selected border's one dark-mode
 * role change, so the recipe remains theme-independent.
 *
 * Every state selector on the FIELD reads Base UI's `Field.Root` attributes through the
 * `group/ti` marker (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`); every state
 * selector on a GRID CELL reads `react-day-picker`'s attributes through the `group/day` marker
 * (`data-selected`, `data-today`, `data-outside`, `data-disabled`). There is no React state behind
 * either set, which also means the states are reachable from a story by rendering.
 *
 * Both sets are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the same discipline
 * every other recipe in this layer uses. Precedence, highest first:
 *
 *   field:  disabled → invalid → focused → hover → resting
 *   cell:   disabled → selected → today → outside → hover → resting
 *
 * ── Why none of this imports `TextInput.recipe` ────────────────────────────────────────────────
 *
 * The field geometry below is a near-copy of `textInputBoxRecipe`'s, and importing it would be
 * less code. It is restated anyway, for the two reasons `TextArea.recipe` sets out at length: the
 * `ds-input` class string would drag the first-generation palette basis back in, and each
 * component owning its own slot namespace is what stops a change made for one breaking another.
 *
 * This is the THIRD hand-written copy of that geometry, which is one more chance to drift than the
 * decision was first taken against — and it DID drift, which is most of what this port fixed.
 * `DatePicker.test.tsx` therefore pins it the same way `TextArea.test.tsx` does — importing both
 * recipes and comparing the radius, border width and min-height they emit for every `position`.
 * The pin was real and it still passed throughout the drift, because it compares the three values
 * that never moved; the ones that moved (the focus stroke, the hover fill, the label's shrunk
 * scale, the control's padding) were not in it. They are now.
 *
 * ── PORTED 2026-09-17 from `Date Picker` 1438:129727 ───────────────────────────────────────────
 *
 * This component was built with no drawing at all, and the header here used to say so. The set now
 * exists, and it was created FROM this implementation — so the interesting part of the port was
 * never the parts that agreed, it was the handful that did not.
 *
 * What moved, and why the direction is unambiguous: the set's description says the date picker is
 * "built from Text Input so the two stack flush in one group", and `.ds-input`/`textInputBoxRecipe`
 * have already been through this same port. So wherever this file disagreed with Text Input, Text
 * Input was right and this file was a stale copy of the treatment `TextArea` still carries.
 *
 *   focus border     1.5px `--border-focus` + a 2px inner tint  ->  a flat 2px `--border-accent`
 *   hover            border only                                ->  border AND fill
 *   error/disabled   thickened to 1.5px                         ->  stay at 1px, as drawn
 *   resting fill     `--background-primary`                     ->  `--background-control`
 *   shrunk label     11/14 at y=10                              ->  12/14 at y=9
 *   value baseline   `pt-[28px]`                                ->  `pt-[29px]`
 *   popup radius     20px (recorded as a token gap)             ->  16px, which IS `radius/md`
 *   nav + trigger    the field's foregrounds                    ->  the muted BUTTON's
 *   trailing button  `right-[20px]`                             ->  `right-[24px]`, the gutter
 *   empty field      label permanently shrunk, hint always on   ->  resting label, hint on focus
 *   nav glyph        the library's own chevron                  ->  `CaretLeft` / `CaretRight`
 *   calendar glyph   a hand-drawn outline calendar              ->  `CalendarBlank`
 *   calendar offset  `sideOffset={8}`                           ->  4
 *
 * The calendar half came back almost entirely accepted: the 40px cell, the 32px nav button, the
 * 12px weekday, the fixed six-week grid and all six cell colours are exactly what the drawing
 * binds. See the `.ds-datepicker` block, which records the same list against the token layer.
 *
 * ── Focus follows the shared text-field treatment ──────────────────────────────────────────────
 *
 * The drawing's `State=Focus` cell answers a keyboard focus with the library's shared `Focused`
 * EFFECT STYLE — two layers, `effect/focus-inner` at spread 2 and `effect/focus-outer` at spread 4.
 * Text inputs also match `:focus-visible` after a pointer click, so attaching that effect directly
 * would combine Focus and Active on every click. Like `TextInput` and `TextArea`, this field uses
 * the 2px Active accent edge as its accessible focus indicator until input-modality tracking can
 * distinguish keyboard focus for the whole field family.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-datepicker` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders as a SIBLING of the box would otherwise fall outside their scope
 * and resolve to inherited colour instead of failing visibly.
 *
 * The group marker is `group/ti` rather than a name of its own, shared with `TextInput` and
 * `TextArea` on purpose: the three are alternative fields, never nested one inside another, so
 * there is no ambiguity to resolve — and sharing it means a stacked group's state selectors read
 * the same whichever field type is in it.
 */
export const datePickerRootRecipe = cva(['group/ti ds-datepicker', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own — all of it is on the `<input>`, exactly as on `TextInput` and
 * `TextArea` and for the same reason: padding the box would leave the input a strip inside a
 * larger box, so clicking near the edges would do nothing and the caret would land in the wrong
 * place. The only exception is the bottom inset when helper text is present, which the control can
 * no longer pay for because the helper sits below it.
 *
 * `min-h-[60px]` is `TextInput`'s floor rather than `TextArea`'s 100px, because this is a
 * single-line field: 1 + 29 + 20 + 9 + 1, which is also the 60px every field cell in the drawing
 * measures. The pin test asserts the match.
 *
 * ── Why the stroke is drawn with a shadow, not the border ─────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state, for the reason `textInputBoxRecipe` spells out:
 * a border is layout, so thickening it on focus moves the padding box and shifts the absolutely
 * positioned label, the value text and — unique to this field — the calendar button pinned to the
 * right gutter. So the border stays at its resting 1px and the focused state's second pixel is
 * painted as an inset `box-shadow`, which occupies no layout space:
 *
 *   inset 0 0 0 1px  --ds-datepicker-stroke-extra   the pixel that makes the focused stroke 2px
 *
 * The slot defaults to `transparent`, which is what lets the shadow rule be unconditional — only
 * the slot is switched, and only by focus.
 *
 * This used to be two layers: a 0.5px thickening reaching 1.5px, plus a 2.5px translucent tint
 * washed inside it, and error and disabled thickened too. The drawing has none of that. Every
 * field cell measures 1px except `State=Active`, which measures a flat 2px in `--border-accent`,
 * and no cell in the set carries an inner tint. `textInputBoxRecipe` already draws it this way.
 *
 * The calendar trigger remains a separate focusable button and uses the shared focus ring. The
 * field box does not: doing so would combine Active and Focus on an ordinary pointer click, unlike
 * the sibling text fields this component is designed to stack with.
 */
export const datePickerBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[60px]',
    'border-solid border-[length:var(--ds-datepicker-border-width)] border-[var(--ds-datepicker-border)]',
    'bg-[var(--ds-datepicker-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * Hover is excluded from every other state so it can never fight them, and it moves the FILL
     * as well as the border — `State=Hover` fills at `--background-control-hover` in the drawing.
     *
     * The pointer-capability media guard is load-bearing and easy to lose. Tailwind's built-in
     * hover variant adds that guard automatically, but an arbitrary variant with a hand-written
     * hover selector does not. Without it, a tap on a touch device leaves the hovered fill and
     * border stuck until the user taps something else.
     *
     * The nav button below already gets this from Tailwind's built-in variant, so this file had the
     * two behaviours side by side before the guard was added here.
     */
    '[@media(hover:hover)]:group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-datepicker-border-hover)]',
    '[@media(hover:hover)]:group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:bg-[var(--ds-datepicker-bg-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-datepicker-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-datepicker-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-datepicker-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-datepicker-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Off by default, so the rule below can be unconditional.
    '[--ds-datepicker-stroke-extra:transparent]',
    'shadow-[inset_0_0_0_1px_var(--ds-datepicker-stroke-extra)]',

    // The second pixel, in the same colour as the focused border. FOCUS ONLY: error and disabled
    // are drawn at 1px and used to be thickened here, which made an errored field's edge heavier
    // than the drawing's and heavier than the text input beside it.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-datepicker-stroke-extra:var(--ds-datepicker-border-focus)]',
  ],
  {
    variants: {
      /**
       * Figma's `Position` axis, pinned to `TextInput`'s values by the test.
       *
       * All FOUR are now drawn. This comment used to say `middle` had no cell and was
       * extrapolated; the reworked set has `Position=Middle` at every state, squared on all four
       * corners, which is what was already here. See `TextInputPosition`, which this component
       * imports rather than re-declaring.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
        // A field in a side-by-side row. The test pins these to `textInputBoxRecipe`, which says
        // why each value squares its other corners explicitly.
        'bottom-start': 'rounded-t-none rounded-ee-none rounded-es-[16px]',
        'bottom-end': 'rounded-t-none rounded-es-none rounded-ee-[16px]',
        start: 'rounded-e-none rounded-s-[16px]',
        end: 'rounded-s-none rounded-e-[16px]',
      },
      /**
       * The bottom inset, which moves between the box and the control when a helper row exists.
       *
       * `7px`, matching `textInputBoxRecipe`. The drawing puts the helper's 20px line box 53px
       * from the top of a 60px box that grows to 82 — 8px of padding under it — and the field
       * family resolves that to 7 here plus the control's own 9. It was 10/10 before the port,
       * which made an errored date field two pixels taller than the text input beside it.
       */
      hasHelper: {
        true: 'pb-[7px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Mechanically identical to `textInputLabelRecipe` — same 19px resting offset, same 9px shrunk
 * offset, same 12/14 scale, same composited `translate`/`scale` animation — so the field types
 * stacked in one group have their labels on the same lines. That alignment is the reason to copy
 * the numbers rather than re-derive them.
 *
 * The shrunk pair moved in the port. It was `translate-y-[10px] scale-[0.785714]`, an 11px label
 * on the 14px base; the drawing's filled cells put a 12px `Body/sm` label with its 20px line box
 * 10px from the outer edge of the box, which is 9px inside the 1px border at 12/14. So the label
 * was both a pixel low and a pixel small against every text input it could be stacked with.
 *
 * `max-w` clears the calendar button as well as the two 24px gutters, which is why it subtracts
 * more than the sibling fields do: the drawing gives the label 222px inside a 298px padding box —
 * 24px gutter, 24px button, 4px gap, 24px gutter — so 76, not the 74 that was here.
 *
 * Deliberately NOT `pointer-events-none`. This is a real `<label>` wired to the control by `Field`,
 * so a click on it focuses the input natively; making it click-through would instead drop clicks on
 * the part of it that overhangs the control.
 */
export const datePickerLabelRecipe = cva([
  'absolute top-0 left-[44px] origin-left cursor-text select-none',
  'font-sans text-body-md text-[var(--ds-datepicker-label)]',
  'max-w-[calc(100%_-_68px)] truncate',
  'translate-y-[19px] scale-100',

  // `translate` and `scale`, not `transform`: Tailwind v4's utilities set the standalone CSS
  // properties of those names, so a transition on `transform` would match nothing and the label
  // would jump to its shrunk position before animating its size. `scale-100` is stated on the base
  // because interpolating from `scale: none` is not reliably animatable.
  FLOATING_LABEL_MOTION,

  // 12/14 — the shrunk size as a ratio of the resting one, since the font-size itself never changes.
  'group-data-[filled]/ti:translate-y-[9px] group-data-[filled]/ti:scale-[0.857143]',
  'group-data-[focused]/ti:translate-y-[9px] group-data-[focused]/ti:scale-[0.857143]',

  /**
   * There is deliberately no third rule here for a visible placeholder.
   *
   * There used to be: `group-has-[:placeholder-shown]`, which shrank the label whenever the field
   * was empty, because the field always rendered `DD/MM/YYYY` and the resting label would have
   * drawn straight through it. The effect was that a date field NEVER showed a resting label —
   * it opened already shrunk, with the format hint on the value line.
   *
   * The drawing does not do that. `State=Default, isFilled=No` is a 14px label centred on the
   * value line with no placeholder at all, and `DD/MM/YYYY` appears only from `State=Active`. So
   * the overlap is solved at the source instead: the placeholder is transparent until the field
   * is focused (see `datePickerControlRecipe`), which means `:placeholder-shown` no longer
   * describes anything the user can see and the label's two ordinary rules are enough.
   *
   * This is also what makes the date field match `TextInput` at rest. The 60px text input renders
   * no placeholder at all, which is the only reason its label could rest centred; this field gets
   * to keep the hint AND the resting label.
   */
  'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-datepicker-label-disabled)]',
]);

/** The required asterisk — `textInputRequiredMarkRecipe`'s rule, on this field's own slots. */
export const datePickerRequiredMarkRecipe = cva([
  'text-[var(--ds-datepicker-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-datepicker-label-disabled)]',
]);

/**
 * The `<input>`, which is also most of the field's hit area.
 *
 * `pt-[28px]` puts the value's 20px line box where `TextInput` and `TextArea` draw theirs, so the
 * three agree when stacked. Everything above that line is still part of the control rather than
 * dead space on the box.
 *
 * `pr-[52px]` is `textInputControlRecipe`'s `hasRightIcon` inset, and the drawing measures the
 * same: the value text ends 52px from the padding box's right edge, clearing the 24px button in
 * the 24px gutter with 4px of air. It was `pr-[50px]`, derived against the old `right-[20px]`
 * button position; both moved together.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own input styling is
 * still in play and every reset is spelled out rather than assumed.
 */
export const datePickerControlRecipe = cva(
  [
    'm-0 block w-full appearance-none border-0 bg-transparent',
    'font-sans text-body-md text-[var(--ds-datepicker-value)]',
    PHONE_INPUT_SIZE,
    'outline-none',
    'pt-[29px] pr-[24px] pl-[44px]',
    'cursor-text',
    /**
     * The format hint, which is HIDDEN until the field is focused.
     *
     * The drawing shows `DD/MM/YYYY` only from `State=Active` onwards; at rest an empty date
     * field is a 14px floating label and nothing else. Hiding it with `text-transparent` rather
     * than by adding or removing the attribute keeps it a pure CSS state, so there is no focus
     * state in React and no frame where the attribute is mid-swap — and the input still reports
     * `:placeholder-shown`, which is what an autofill heuristic reads.
     *
     * Disabled deliberately never reveals it: a disabled field cannot be focused, so the rule
     * below simply never matches there.
     */
    'placeholder:text-transparent',
    'group-data-[focused]/ti:placeholder:text-[var(--ds-datepicker-placeholder)]',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-value-disabled)]',
    // Edge and Chrome add their own clear affordance, which would sit beside the calendar button.
    '[&::-ms-clear]:hidden',
  ],
  {
    variants: {
      /** When helper text follows, the bottom inset moves to the box — see `datePickerBoxRecipe`. */
      hasHelper: {
        true: 'pb-0',
        false: 'pb-[9px]',
      },
    },
    defaultVariants: { hasHelper: false },
  }
);

/**
 * The button that opens the calendar.
 *
 * ── Why it is 24px and not the 16px the field family's icon slot uses ─────────────────────────
 *
 * `textInputAffixRecipe` draws a 16px cell at `right-[24px] top-[21px]`. That is the right size
 * for a GLYPH, and `TextInput` uses it for one; here the cell is a real button, and a 16×16 target
 * is under WCAG 2.5.8's 24×24 minimum. The drawing agrees and goes further: it makes the
 * affordance a literal `Button Size=Small Type=Muted iconOnly` — a 24×24 round box, 4px of padding,
 * a 16px `CalendarBlank` inside.
 *
 * ── Where it sits, which the port moved ───────────────────────────────────────────────────────
 *
 * `right-[24px]`, not the `right-[20px]` that was here. The old number came from centring the
 * BUTTON on the point the affix slot would have put the GLYPH, which put the button's own edge
 * 4px into the gutter. The drawing aligns the button box with the 24px gutter like everything else
 * in the field, which lands the glyph 28px from the edge rather than 24 — and that is also what
 * `textInputTrailingButtonRecipe` already does (`right-[24px] top-[17px]`, verbatim). So the
 * trailing button of a date field and the trailing button of a text input now line up in a
 * stacked group, which they did not before.
 *
 * ── It moved to the START of the field on 2026-10-01 ──────────────────────────────────────────
 *
 * It sat at the far end, so a host who clicked the field to pick a date had to cross the whole
 * width of a full-width field to reach the one control that opens the calendar. The field now opens
 * its calendar on a click anywhere in it, and the button leads the value: `left-[16px]` puts the
 * 16px glyph 21px from the outer edge (1px border, then a 24px button with the glyph centred), and
 * the text starts at 44px, an 8px gap. The hit area grows 10px into the left gutter and only 4px
 * right, which is the slack before the text.
 *
 * The button is still AFTER the input in the DOM, so Tab goes input → button although the button is
 * drawn to the left. That is kept on purpose: the input's Tab hand-off focuses the box's button
 * after the input, and moving it earlier would put the button first in the tab order of a field
 * whose main control is the input. Reorder the DOM and that hand-off together if this changes.
 *
 * `focus-visible:` rather than the box's `has-[input:focus-visible]`, because this element CAN be
 * focused, and because the whole point of putting an outline here is to distinguish reaching the
 * button from reaching the input — which is also why the box's own outline deliberately ignores
 * this element rather than lighting up around it. Button's three utilities verbatim, which is the
 * system's one focus treatment.
 */
export const datePickerTriggerRecipe = cva([
  'absolute top-[17px] left-[16px] inline-flex size-[24px] items-center justify-center',
  'm-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0',
  'text-[var(--ds-datepicker-trigger-fg)]',
  'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-trigger-fg-disabled)]',
  focusRing(),
  '[&_svg]:size-[16px]',

  // No 300ms double-tap-zoom wait before the calendar opens on a phone.
  'touch-manipulation',

  /**
   * The HIT AREA, grown past the 24px the drawing gives the button.
   *
   * 24x24 clears WCAG 2.5.8's minimum, which is the bar Natter is actually held to, but it is
   * under the 44px comfort target and this is the control a thumb reaches for. The button's drawn
   * size is fixed by the Figma cell, so the target grows through a pseudo-element instead and
   * nothing visible moves.
   *
   * It grows as far as it can without stealing from the text: 44px tall (10px each way, which
   * still sits inside the 60px box) and 38px wide — 10px into the empty right gutter, 4px left,
   * which is the slack between the button and where `pr-[52px]` stops the value text. A true
   * 44x44 would reach under the last characters of a typed date and open the calendar instead of
   * placing the caret, which is a worse failure than a narrow target.
   */
  "after:absolute after:-inset-y-[10px] after:-right-[4px] after:-left-[10px] after:content-['']",
]);

/**
 * Helper / error text, rendered inside the border as the other two fields do.
 *
 * Inside rather than below, and that placement is what makes the stacked group work:
 * `FieldGroup` collapses the borders of adjacent CHILDREN, so anything rendered outside the box
 * would sit between two borders that are supposed to be one line.
 */
export const datePickerHelperRecipe = cva([
  'flex items-center',
  // `m-0` is load-bearing rather than tidiness: `Field.Description` renders a `<p>`, and with
  // Preflight absent it keeps the browser's default `margin: 1em 0`, which the `<div>` that
  // `Field.Error` renders does not have — so the box would change height with the state.
  'm-0',
  'pr-[24px] pl-[44px]',
  'font-sans text-body-sm text-[var(--ds-datepicker-helper)]',
  // Scoped off disabled, and with a disabled colour of its own — both copied from
  // `textInputHelperRecipe`, and both missing here before the port. An invalid field that was then
  // disabled kept its red helper line at full strength while every other part of the box greyed
  // out, which is the prop-versus-DOM split this layer otherwise avoids.
  'group-[[data-invalid]:not([data-disabled])]/ti:text-[var(--ds-datepicker-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-datepicker-label-disabled)]',
]);

/** The positioner. `z-[1400]` is the layer `Select` puts its menu on, above every MUI surface. */
export const datePickerPositionerRecipe = cva(['z-[1400]']);

/**
 * The calendar popup.
 *
 * `ds-datepicker` is repeated here by the component rather than inherited: the popup is portalled
 * into `<body>`, so it has none of the field's ancestors and would otherwise resolve every slot to
 * nothing. The theme class is NOT repeated — `DatePicker` puts it on `Popover.Portal`, which is an
 * ancestor of this element, so the ordinary cascade reaches it. `selectMenuRecipe` documents the
 * same pair of facts.
 *
 * `max-h-[var(--available-height)]` plus scrolling keeps the calendar on screen near the bottom of
 * the viewport; Base UI computes the value from the collision boundary.
 *
 * ── The radius was a recorded design-system gap, and it is not one ────────────────────────────
 *
 * This popup was drawn here at 20px, and 20 is not in the `⭕️ Radius` collection (6 / 16 / 24 /
 * full), so it went into the gap log as "the calendar needs a radius token that does not exist".
 * `Date Picker_Calendar` 1438:130281 measures **16** — `radius/md`, the same radius the field uses.
 * The gap was an artefact of the code's invented value, not a real hole in the collection.
 *
 * Both records have been corrected rather than deleted, so nobody repeats the ask to design: the
 * gap log entry, and the set's own description, which claimed the missing token and now carries
 * the correction (2026-09-17).
 */
export const datePickerPopupRecipe = cva([
  'ds-datepicker',
  'box-border flex flex-col',
  'ds-popup-scroll max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'p-[16px]',
  'rounded-[16px]',
  'border-solid border-[length:1px] border-[var(--ds-datepicker-popup-border)]',
  'bg-[var(--ds-datepicker-popup-bg)]',
  'outline-none',

  /**
   * ── The entrance and the exit ────────────────────────────────────────────────────────────────
   *
   * The same overlay motion `menuPanelRecipe` and `selectMenuRecipe` carry, for the same reason the
   * three share their geometry: a calendar opening off a field is the same kind of event as a list
   * opening off one, and the user should not be able to tell from the motion which is which.
   * `--motion-overlay` in, `--motion-overlay-exit` out, origin-aware, scale gated on `motion-safe:`.
   *
   * The reasoning is written once, on `menuPanelRecipe`.
   *
   * A calendar is the largest of the three panels, which is the one argument for reaching for
   * `--motion-surface` instead. It is not taken: duration tracks how OFTEN a thing is seen before
   * it tracks how big it is, and this panel opens from a field the same way the other two do. A
   * calendar that took 250ms while the select beside it took 200 would read as the slower control,
   * not as the larger one.
   */
  '[transform-origin:var(--transform-origin,center)]',
  'transition-[opacity,scale] duration-[var(--motion-overlay)] ease-[var(--motion-overlay-ease)]',
  'data-[ending-style]:duration-[var(--motion-overlay-exit)]',
  'data-[ending-style]:ease-[var(--motion-overlay-exit-ease)]',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  'motion-safe:data-[starting-style]:scale-[0.96] motion-safe:data-[ending-style]:scale-[0.96]',
]);

/**
 * The `classNames` map handed to `react-day-picker`.
 *
 * A plain object rather than a `cva`, because there is nothing to vary: every key is a fixed class
 * string and the library merges the map over its own defaults. Every visual value the calendar
 * draws is in here, which is what keeps `DatePicker.tsx` free of appearance.
 *
 * ── The keys are v9's, and v8's are silently ignored ──────────────────────────────────────────
 *
 * Version 9 renamed the element keys wholesale: v8's `cell` is v9's `day`, v8's `day` is v9's
 * `day_button`, and the state keys moved from `day_selected`/`day_outside` to `selected`/`outside`.
 * The v8 names still TYPE-CHECK — the library keeps them in a `DeprecatedUI` map so an upgrade
 * reports something useful — so a map written against v8 documentation compiles cleanly and styles
 * nothing. `UI`, `DayFlag` and `SelectionState` are imported as the source of truth rather than
 * spelled as string literals, so a rename in a future major fails the build instead of the eye.
 *
 * ── Why the state classes hang off the CELL and not the button ────────────────────────────────
 *
 * The library puts `data-selected`, `data-today`, `data-outside` and `data-disabled` on the `<td>`
 * and renders the `<button>` inside it. So the `<td>` carries a `group/day` marker and every state
 * rule below is a `group-[...]/day:` variant on the button. Styling the `<td>` directly would work
 * for colour but not for the ring or the hit area, both of which belong to the button.
 */
/**
 * The two month chevrons.
 *
 * 32px round, which the drawing confirms — they are `Button Size=Small Type=Muted iconOnly`
 * instances, and Small IS 32. What the drawing changed is the COLOUR ROLES behind them, not the
 * geometry: a muted button's foreground is `--foreground-primary` and its hover is the
 * translucent on-surface overlay, where this component was feeding it the field's secondary
 * foreground and the opaque control-hover step. Both moved in `.ds-datepicker`; the slot names
 * here are unchanged, which is the indirection working.
 *
 * Deliberately NOT a `Button` instance, though the drawing makes it one. `react-day-picker`
 * renders these itself and takes a class string, not a component, for the previous/next
 * controls — so a real `Button` would mean overriding two more of the library's internals to gain
 * nothing visible. If the nav is ever lifted out of the library, make them `Button`s.
 */
const navButton = [
  'relative inline-flex size-[32px] items-center justify-center',
  // No double-tap-zoom wait before the month changes on a phone.
  'touch-manipulation',
  // Phones get an invisible 44px-tall touch target; the drawn 32px chevron does not move. Height only: the
  // previous and next arrows touch, and a wider target would take each other's edge.
  "max-sm:before:absolute max-sm:before:inset-x-0 max-sm:before:top-1/2 max-sm:before:h-11 max-sm:before:-translate-y-1/2 max-sm:before:content-['']",
  'm-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0',
  'text-[var(--ds-datepicker-nav-fg)]',
  'transition-[background-color,color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'hover:not-disabled:bg-[var(--ds-datepicker-nav-bg-hover)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-nav-fg-disabled)]',
  focusRing(),
].join(' ');

export const datePickerCalendarClassNames: Partial<ClassNames> = {
  [UI.Root]: 'flex flex-col',
  [UI.Months]: 'relative flex flex-col',
  [UI.Month]: 'flex flex-col gap-[4px]',

  /**
   * Month name on the left, both chevrons together on the right.
   *
   * The nav has to come out of flow whatever the arrangement, because the library renders it as a
   * sibling of the months rather than inside the caption row. What it must NOT do is span the
   * full width with the caption underneath it: an earlier revision did exactly that, to put one
   * chevron either side of the month name, and axe filed the month heading under `incomplete`
   * with an undeterminable background — a transparent 32px box laid over text is still an
   * overlap, and an undeterminable contrast is not a contrast that has been checked. Pinning the
   * nav to the right removes the overlap rather than suppressing the report.
   */
  [UI.Nav]: 'absolute top-0 right-0 flex h-[32px] items-center gap-[4px]',
  [UI.MonthCaption]: 'flex h-[32px] items-center justify-start pl-[8px]',
  [UI.CaptionLabel]: 'font-sans text-label-lg text-[var(--ds-datepicker-caption-fg)]',

  [UI.ButtonPrevious]: navButton,
  [UI.ButtonNext]: navButton,

  /**
   * Reaches `NavChevron` in `DatePicker.tsx`, which renders the design system's `CaretLeft` and
   * `CaretRight` in place of the library's own glyph — the drawing names both.
   *
   * `fill-current` used to be here and has been dropped. It was load-bearing against the
   * library's chevron, a bare `<polygon>` with no `fill` attribute that paints BLACK rather than
   * inheriting; the design system's icons already carry `fill="currentColor"` as an ATTRIBUTE, so
   * the button's `text-` utility reaches them unaided. Do not add a CSS `fill` rule back: a CSS
   * declaration beats a presentational attribute, which is how a Phosphor glyph renders laid out,
   * sized, accessible and completely blank.
   */
  [UI.Chevron]: 'size-[16px]',

  [UI.MonthGrid]: 'w-full border-collapse',
  [UI.Weekdays]: '',
  [UI.Weekday]: [
    'size-[40px] p-0 text-center align-middle',
    'font-sans text-label-md text-[var(--ds-datepicker-weekday-fg)]',
  ].join(' '),
  [UI.Weeks]: '',
  [UI.Week]: '',

  /** The `<td>`. Carries the state attributes, so it carries the group marker. */
  [UI.Day]: 'group/day p-0 text-center',

  /**
   * The `<button>` in each cell.
   *
   * ── The hit area, which the design now specifies ──────────────────────────────────────────
   *
   * 40x40, and this note used to say nothing in the system implied it. `.Date Picker_Day`
   * 1438:130280 draws exactly 40x40, round, and its description gives a better reason than the
   * one reasoned to here: 40 is `Button`'s Medium size, and a selected day IS visually a Primary
   * button, so the two share geometry as well as tokens. The reasoning below still holds and is
   * kept because it is what the accessibility floors actually require — WCAG 2.5.8 asks 24x24 as
   * a minimum and 2.5.5 asks 44x44 as an enhancement; 40 clears the requirement, keeps a
   * seven-column month inside the 280px a popover can take without scrolling at a 320px viewport,
   * and matches the 40px row the platform date pickers this will be compared against use.
   * `DatePicker.stories.tsx` measures it for real rather than trusting the utility.
   *
   * Why this is not literally a `Button`, per the set's own description: four of the six states
   * map exactly, but TODAY needs an accent ring with an accent number and OUTSIDE needs a
   * secondary number, and no Button Type expresses either. If one ever does, this set should go.
   *
   * ── The border is always there ────────────────────────────────────────────────────────────
   *
   * `border-transparent` at rest, recoloured for selected and today. A border that appeared only
   * in either state would make its number 1px smaller than every other number in the grid and
   * shift the whole row, which is the same layout-versus-paint trap the field's stroke avoids —
   * here it is cheaper to simply always reserve the pixel.
   */
  [UI.DayButton]: [
    'relative flex size-[40px] items-center justify-center',
    // No double-tap-zoom wait before a date is picked — the densest tap surface in the system.
    'touch-manipulation',
    'm-0 cursor-pointer appearance-none rounded-full p-0',
    'border-solid border-[length:var(--ds-datepicker-day-border-width)] border-transparent',
    'bg-[var(--ds-datepicker-day-bg)]',
    'font-sans text-body-sm text-[var(--ds-datepicker-day-fg)]',
    'transition-[background-color,color,border-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * Hover: fill only, and excluded from selected and disabled so it can never fight them. It
     * deliberately does NOT recolour the number, which is what lets it coexist with today's and
     * the outside month's foregrounds without a fourth exclusion on each.
     *
     * Touch-guarded for the reason the field's hover explains — and this is the worst surface in
     * the system to get it wrong on: forty-two cells in a 280px square, so tapping a date on a
     * phone left a stuck grey fill on the day you just picked, one cell away from the blue one
     * that means "selected".
     */
    '[@media(hover:hover)]:group-[:hover:not([data-disabled]):not([data-selected])]/day:bg-[var(--ds-datepicker-day-bg-hover)]',

    // Selected beats everything but disabled.
    'group-[[data-selected]:not([data-disabled])]/day:bg-[var(--ds-datepicker-day-bg-selected)]',
    'group-[[data-selected]:not([data-disabled])]/day:border-[var(--ds-datepicker-day-border-selected)]',
    'group-[[data-selected]:not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-selected)]',

    // Today is a ring plus a recoloured number, suppressed once the day is selected: a blue ring
    // drawn on the solid blue selected fill is invisible, and drawing it anyway would imply a
    // state that is not being shown.
    'group-[[data-today]:not([data-selected]):not([data-disabled])]/day:border-[var(--ds-datepicker-day-border-today)]',
    'group-[[data-today]:not([data-selected]):not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-today)]',

    // A day from the neighbouring month is still selectable, so it is stepped back rather than
    // washed out. Excluded from today, because "today, in the month you are not looking at" is
    // more useful information than "not this month".
    'group-[[data-outside]:not([data-today]):not([data-selected]):not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-outside)]',

    'group-data-[disabled]/day:cursor-not-allowed',
    'group-data-[disabled]/day:text-[var(--ds-datepicker-day-fg-disabled)]',

    focusRing(),
  ].join(' '),

  /** A hidden day still occupies its cell, so the grid keeps its shape. */
  [DayFlag.hidden]: 'invisible',

  /**
   * Named and left EMPTY on purpose, rather than omitted.
   *
   * The library appends these state classes to the `<td>` as well as setting the matching
   * `data-*` attributes, and its defaults (`rdp-selected`, `rdp-today`, …) would otherwise survive
   * into the markup. They style nothing — this component never imports the library's stylesheet —
   * but a class named after a state that carries no styling is exactly the thing a future reader
   * writes a rule against, and it would then be the one rule in this component not resolved from
   * a role. Every state above is drawn from the `data-*` attribute instead.
   */
  [SelectionState.selected]: '',
  [DayFlag.today]: '',
  [DayFlag.outside]: '',
  [DayFlag.disabled]: '',
  [DayFlag.focused]: '',
};
